/**
 * Transformers for converting aggregator API responses to normal application data structures
 * These utilities ensure aggregator responses can be displayed using existing application components
 */

import { ProviderOffersI } from 'src/e2e-redesign/business-logic/e2e-configuration-object-logic';
import { LendersI } from 'src/interfaces/lenders.interfaces';
import {
  ApplicationSubmissionStateDto,
  AppSubLenderResponseE,
  AppSubStatusE,
} from 'src/interfaces/submissions.interfaces';
import { AggregatorStatusResponse } from 'src/interfaces/aggregator.interfaces';
import { AGGREGATOR_STATUS } from './aggregator.constants';

/**
 * Maps aggregator lender_response string to AppSubLenderResponseE enum
 */
export const mapAggregatorLenderResponse = (response: string): AppSubLenderResponseE => {
  switch (response) {
    case AGGREGATOR_STATUS.LENDER_RESPONSE.APPROVED:
      return AppSubLenderResponseE.approved;
    case AGGREGATOR_STATUS.LENDER_RESPONSE.PRE_APPROVED:
      return AppSubLenderResponseE.pre_approved;
    case AGGREGATOR_STATUS.LENDER_RESPONSE.DECLINED:
      return AppSubLenderResponseE.rejected;
    case AGGREGATOR_STATUS.LENDER_RESPONSE.PENDING:
      return AppSubLenderResponseE.pending;
    default:
      return AppSubLenderResponseE.pending;
  }
};

/**
 * Maps aggregator submission_state string to AppSubStatusE enum
 */
export const mapAggregatorSubmissionState = (state: string): AppSubStatusE => {
  switch (state) {
    case AGGREGATOR_STATUS.SUBMISSION_STATE.PENDING:
      return AppSubStatusE.submitted;
    case AGGREGATOR_STATUS.SUBMISSION_STATE.PROCESSING:
      return AppSubStatusE.processing;
    case AGGREGATOR_STATUS.SUBMISSION_STATE.COMPLETED:
      return AppSubStatusE.completed;
    case AGGREGATOR_STATUS.SUBMISSION_STATE.FAILED:
      return AppSubStatusE.error;
    case AGGREGATOR_STATUS.SUBMISSION_STATE.LENDER_RESPONDED:
      return AppSubStatusE.lender_responded;
    default:
      return AppSubStatusE.submitted;
  }
};

/**
 * Transforms AggregatorStatusResponse to ApplicationSubmissionStateDto
 * Uses default values for fields not available in aggregator context
 */
export const transformAggregatorStatusToSubmissionDto = (
  statusData: AggregatorStatusResponse,
): ApplicationSubmissionStateDto => {
  return {
    submission_id: statusData.submission_id,
    lender_response: mapAggregatorLenderResponse(statusData.lender_response),
    submission_state: mapAggregatorSubmissionState(statusData.submission_state),
    lender_offers: statusData.lender_offers || [],
    lender_id: Number(statusData.lender_id),
    lender_name: statusData.lender_name || 'Lender',
    lender_approved_message: statusData.lender_approved_message || undefined,
    lender_pending_message: statusData.lender_pending_message || undefined,
    lender_declined_message: statusData.lender_declined_message || undefined,
    contract_status: (statusData.contract_status as any) || undefined,
    submission_date: statusData.submission_date || new Date().toISOString(),
    // Required fields with defaults for aggregator context
    // These fields are not available in aggregator flow but are required by ApplicationSubmissionStateDto
    errors: { error_type: 'init' as any, error_messages: [], error_details: {}, prevent_api_pipe: false },
    log: [],
    is_submitted: true,
    form_id: 0,
    store_id: 0,
    org_id: 0,
    submitted_user_id: 0,
  };
};

/**
 * Transforms AggregatorStatusResponse to LendersI
 * Creates a minimal lender object with available data
 */
export const transformAggregatorStatusToLender = (statusData: AggregatorStatusResponse): LendersI => {
  return {
    lender_id: Number(statusData.lender_id),
    name: statusData.lender_name || 'Lender',
    is_active: true,
    logo_url: statusData.lender_logo || undefined,
    // Required fields with defaults for aggregator context
    default_settings: {} as any,
    api_pipe_settings: {} as any,
    laa_settings: {} as any,
  };
};

/**
 * Transforms AggregatorStatusResponse to ProviderOffersI
 * Combines submission details, lender info, and offer details
 */
export const transformAggregatorStatusToProviderOffer = (
  statusData: AggregatorStatusResponse,
  submissionDetails: ApplicationSubmissionStateDto,
  lender: LendersI,
): ProviderOffersI => {
  return {
    sub_details: submissionDetails,
    offer_details: statusData.lender_offers?.[0],
    lender: lender,
    lender_disclosures: undefined,
    is_offer: true,
  };
};

/**
 * Main transformation function that converts AggregatorStatusResponse to all required structures
 * Returns an object containing submissionDetails, lender, and offer for use with NewDumbLenderResponseTable
 */
export const transformAggregatorStatusResponse = (
  statusData: AggregatorStatusResponse | null,
): {
  submissionDetails: ApplicationSubmissionStateDto;
  lender: LendersI;
  offer: ProviderOffersI;
} | null => {
  if (!statusData) {
    return null;
  }

  const submissionDetails = transformAggregatorStatusToSubmissionDto(statusData);
  const lender = transformAggregatorStatusToLender(statusData);
  const offer = transformAggregatorStatusToProviderOffer(statusData, submissionDetails, lender);

  return { submissionDetails, lender, offer };
};
